/**
 * API Client - Cliente HTTP base para peticiones
 */

import logger from '../../../utils/simpleLogger';

// Helper function for silent debug logging (disabled - no logging server available)
const debugLog = (location: string, message: string, data: any, hypothesisId: string) => {
  // Disabled to prevent ERR_CONNECTION_REFUSED errors in console
  return;
};

export interface ApiClientConfig {
  baseUrl: string;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  refreshAccessToken: () => Promise<boolean>;
  clearTokens: () => void;
}

export class ApiClient {
  private baseUrl: string;
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.config = config;
  }

  /**
   * Método principal para hacer peticiones HTTP
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {};

    // Solo agregar Content-Type si no es FormData
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Agregar token de autenticación si existe
    const token = this.config.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Log warning si no hay token (solo en desarrollo)
      if (import.meta.env.DEV) {
        console.warn('⚠️ [API] No token available for request:', endpoint);
      }
    }

    const fullUrl = `${this.baseUrl}${endpoint}`;

    debugLog('ApiClient.request', 'BEFORE fetch', { endpoint, fullUrl, baseUrl: this.baseUrl, method: options.method || 'GET', hasToken: !!token }, 'A');

    try {
      logger.debug(`🌐 [API] Making request: ${options.method || 'GET'} ${fullUrl}`, {
        endpoint,
        hasBody: !!options.body,
        bodyType: options.body instanceof FormData ? 'FormData' : typeof options.body,
        bodySize: options.body ? (options.body instanceof FormData ? 'FormData' : String(options.body).length) : 0,
        headers: Object.keys(headers),
        hasToken: !!token
      });

      const requestStartTime = Date.now();
      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      const requestElapsedTime = Date.now() - requestStartTime;
      logger.debug(`🌐 [API] Request completed: ${response.status} ${response.statusText} (${requestElapsedTime}ms)`, {
        endpoint,
        url: fullUrl,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        contentType: response.headers.get('content-type')
      });

      debugLog('ApiClient.request', 'AFTER fetch (success)', { endpoint, fullUrl, status: response.status, statusText: response.statusText, ok: response.ok }, 'A');

      // Si la respuesta es 401 y tenemos refresh token, intentar renovar
      if (response.status === 401 && this.config.getRefreshToken()) {
        const refreshed = await this.config.refreshAccessToken();
        if (refreshed) {
          // Reintentar la petición original con el nuevo token
          return this.request(endpoint, options);
        } else {
          // Si no se pudo renovar, limpiar tokens y lanzar error
          this.config.clearTokens();
          throw new Error('Session expired. Please login again.');
        }
      }

      // Try to parse JSON, but handle non-JSON responses gracefully
      let data: any;
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      try {
        if (isJson) {
          data = await response.json();
        } else {
          // If not JSON, try to get text for error message
          const text = await response.text();
          logger.error(`❌ [API] Non-JSON response from ${endpoint}:`, {
            status: response.status,
            statusText: response.statusText,
            contentType,
            textPreview: text.substring(0, 200)
          });
          throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 100)}`);
        }
      } catch (parseError) {
        // If JSON parsing fails, we can't read the response again (stream already consumed)
        // Log what we know and rethrow with better context
        if (parseError instanceof SyntaxError || parseError instanceof TypeError) {
          logger.error(`❌ [API] Failed to parse JSON from ${endpoint}:`, {
            status: response.status,
            statusText: response.statusText,
            contentType,
            parseError: parseError instanceof Error ? parseError.message : String(parseError),
            isJson: isJson
          });
          throw new Error(`Invalid JSON response from server (${response.status}): ${parseError instanceof Error ? parseError.message : String(parseError)}`);
        }
        throw parseError;
      }

      if (!response.ok) {
        const errorMessage = data?.error || data?.message || `HTTP error! status: ${response.status}`;

        // Special handling: 404 on config-info is expected when message doesn't exist yet (race condition)
        // Log as debug instead of error to reduce noise
        const is404ConfigInfo = response.status === 404 && endpoint.includes('/chat/config-info');

        if (is404ConfigInfo) {
          logger.debug(`🔍 [API] Config not found (expected for recent messages) (${endpoint}):`, {
            status: response.status,
            messageId: data?.message_id || 'unknown'
          });
        } else {
          logger.error(`❌ [API] Request failed (${endpoint}):`, {
            status: response.status,
            statusText: response.statusText,
            error: errorMessage,
            data
          });
        }

        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      // Don't log AbortError - it's expected when requests are cancelled (e.g., during debouncing)
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }

      const errorDetails: any = {
        endpoint,
        fullUrl,
        baseUrl: this.baseUrl,
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : 'Unknown',
      };

      // Check for specific error types
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        errorDetails.isNetworkError = true;
        errorDetails.possibleCauses = [
          'Backend server not running',
          'Wrong IP address or port',
          'CORS blocking request',
          'Network connectivity issue'
        ];
      }

      debugLog('ApiClient.request', 'fetch ERROR', errorDetails, 'A,B,C,D');

      // Don't log 404 errors for document endpoints - they're expected when documents are deleted
      // and will be handled gracefully by DocumentSelectionContext
      const isDocumentNotFound =
        error instanceof Error &&
        error.message.includes('Document not found') &&
        endpoint.startsWith('/documents/');

      // Don't log network errors for saved data endpoints - they're expected if backend is unavailable
      // and will be handled gracefully by useDriveData
      const isNetworkError =
        error instanceof TypeError ||
        (error instanceof Error && (
          error.name === 'TypeError' ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.includes('ERR_')
        ));

      const isSavedDataEndpoint =
        endpoint.includes('/saved-messages') ||
        endpoint.includes('/pdf-snippets') ||
        endpoint.includes('/saved-content');

      // Don't log 404 errors for config-info - they're expected when messages don't exist yet
      const isConfigNotFound =
        error instanceof Error &&
        error.message.includes('Mensaje no encontrado') &&
        endpoint.includes('/chat/config-info');

      if (isDocumentNotFound) {
        // Log at debug level instead of error level for document not found errors
        logger.debug(`ℹ️ [API] Document not found (${endpoint}) - this is expected when documents are deleted`);
      } else if (isConfigNotFound) {
        // Don't log config not found errors - they're expected and already logged as debug
        logger.debug(`ℹ️ [API] Config not found (${endpoint}) - this is expected for messages not yet persisted`);
      } else if (isNetworkError && isSavedDataEndpoint) {
        // Don't log network errors for saved data endpoints - they're handled gracefully
        // These errors are expected if the backend is unavailable and the UI should continue working
        logger.debug(`ℹ️ [API] Network error (${endpoint}) - backend may be unavailable`);
      } else if (isNetworkError) {
        // Log network errors once with helpful message
        logger.warn(`⚠️ [API] Backend unavailable (${endpoint}) - Is the server running on ${this.baseUrl}?`);
      } else {
        logger.error(`❌ [API] Error (${endpoint})`, error);
      }

      throw error;
    }
  }
}
